/**
 * A contact's one postal address and its department (P69), shared by the
 * API (validation on create/update), the web app (form, display, demo
 * mode, CSV) and both vCard emitters.
 *
 * `country` is an ISO 3166-1 alpha-2 code ("GB", "US"), the same kind of
 * code as the tenant's default country (`CountryCode` in settings.ts).
 * Writers may send a code in any case or an English country name or
 * common alias ("United Kingdom", "UK", "USA"); it is stored as the code.
 * Anything else is refused, so readers can always turn it into a name
 * with `countryName`.
 */
export const ADDRESS_FIELDS = ['street', 'city', 'region', 'postalCode', 'country'];
/** Longest street (all lines together) the API accepts. */
export const MAX_STREET_LENGTH = 500;
/** Longest department, city, region or postal code the API accepts. */
export const MAX_ADDRESS_PART_LENGTH = 200;
/** Every assigned ISO 3166-1 alpha-2 code. */
export const ISO_COUNTRY_CODES = ('AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS ' +
    'BT BV BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE ' +
    'EG EH ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM ' +
    'HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW KY KZ LA LB LC ' +
    'LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ NA ' +
    'NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW ' +
    'SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO ' +
    'TR TT TV TW TZ UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW').split(' ');
const CODE_SET = new Set(ISO_COUNTRY_CODES);
/** Names people commonly use that aren't the English display name. */
const COUNTRY_ALIASES = {
    uk: 'GB',
    'great britain': 'GB',
    britain: 'GB',
    england: 'GB',
    scotland: 'GB',
    wales: 'GB',
    'northern ireland': 'GB',
    usa: 'US',
    'u.s.': 'US',
    'u.s.a.': 'US',
    america: 'US',
    'united states of america': 'US',
    uae: 'AE',
    holland: 'NL',
    'the netherlands': 'NL',
    'republic of ireland': 'IE',
    eire: 'IE',
    'south korea': 'KR',
    korea: 'KR',
    'north korea': 'KP',
    russia: 'RU',
    'czech republic': 'CZ',
    vietnam: 'VN',
    'ivory coast': 'CI',
    turkey: 'TR'
};
function displayNames(locale) {
    try {
        return new Intl.DisplayNames(locale ? [locale] : ['en'], { type: 'region', fallback: 'none' });
    }
    catch {
        return null;
    }
}
let englishIndex = null;
function englishNameIndex() {
    if (englishIndex)
        return englishIndex;
    const index = new Map();
    const names = displayNames('en');
    if (names) {
        for (const code of ISO_COUNTRY_CODES) {
            const name = names.of(code);
            if (name)
                index.set(foldName(name), code);
        }
    }
    for (const [alias, code] of Object.entries(COUNTRY_ALIASES))
        index.set(foldName(alias), code);
    englishIndex = index;
    return index;
}
function foldName(s) {
    return s
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/&/g, 'and')
        .replace(/\s+/g, ' ')
        .trim();
}
/** True for an assigned ISO 3166-1 alpha-2 code, in upper case. */
export function isCountryCode(value) {
    return CODE_SET.has(value);
}
/**
 * The ISO code for a country given as a code (any case) or an English
 * name or common alias; undefined when it isn't recognised.
 */
export function countryCode(input) {
    const t = (input ?? '').trim();
    if (!t)
        return undefined;
    if (/^[a-z]{2}$/i.test(t)) {
        const upper = t.toUpperCase();
        if (upper === 'UK')
            return 'GB';
        return CODE_SET.has(upper) ? upper : undefined;
    }
    return englishNameIndex().get(foldName(t));
}
/** A country's name for display (the code itself when no name is known). */
export function countryName(code, locale) {
    return displayNames(locale)?.of(code) ?? code;
}
function part(value, field, max) {
    if (value === undefined || value === null)
        return undefined;
    if (typeof value !== 'string')
        return new Error(`address.${field} must be a string`);
    const t = value.trim();
    if (t.length > max)
        return new Error(`address.${field} must be at most ${max} characters`);
    return t || undefined;
}
/**
 * Validate and tidy an address from a request body. Each part is trimmed
 * (street lines individually, blank lines dropped); `country` becomes its
 * ISO code. An address with every part blank, or `null`, is no address.
 */
export function normaliseAddress(input) {
    if (input === undefined || input === null)
        return { ok: true, address: undefined };
    if (typeof input !== 'object' || Array.isArray(input)) {
        return { ok: false, error: 'address must be an object' };
    }
    const b = input;
    const out = {};
    const street = part(b.street, 'street', MAX_STREET_LENGTH);
    if (street instanceof Error)
        return { ok: false, error: street.message };
    if (street) {
        const lines = street
            .split(/\r?\n/)
            .map((l) => l.trim())
            .filter(Boolean)
            .join('\n');
        if (lines)
            out.street = lines;
    }
    for (const field of ['city', 'region', 'postalCode']) {
        const v = part(b[field], field, MAX_ADDRESS_PART_LENGTH);
        if (v instanceof Error)
            return { ok: false, error: v.message };
        if (v)
            out[field] = v.replace(/\s+/g, ' ');
    }
    const country = part(b.country, 'country', MAX_ADDRESS_PART_LENGTH);
    if (country instanceof Error)
        return { ok: false, error: country.message };
    if (country) {
        const code = countryCode(country);
        if (!code) {
            return {
                ok: false,
                error: `address.country must be a two-letter ISO country code (e.g. "GB"); "${country}" isn't one`
            };
        }
        out.country = code;
    }
    return { ok: true, address: hasAddress(out) ? out : undefined };
}
/** True when the address has any part filled in. */
export function hasAddress(a) {
    return !!a && ADDRESS_FIELDS.some((f) => typeof a[f] === 'string' && a[f].trim() !== '');
}
/**
 * The address as display lines: street lines, then "city, region
 * postcode", then the country's name.
 */
export function addressLines(a, locale) {
    if (!hasAddress(a))
        return [];
    const lines = [];
    if (a.street)
        lines.push(...a.street.split('\n').filter(Boolean));
    const place = [a.city, a.region].filter(Boolean).join(', ');
    const placeLine = [place, a.postalCode].filter(Boolean).join(' ');
    if (placeLine)
        lines.push(placeLine);
    if (a.country)
        lines.push(countryName(a.country, locale));
    return lines;
}
/**
 * Validate a department from a request body: trimmed; blank or null is
 * none. Throws on a non-string or one that's too long.
 */
export function normaliseDepartment(value) {
    if (value === undefined || value === null)
        return undefined;
    if (typeof value !== 'string')
        throw new Error('department must be a string');
    const t = value.trim().replace(/\s+/g, ' ');
    if (t.length > MAX_ADDRESS_PART_LENGTH) {
        throw new Error(`department must be at most ${MAX_ADDRESS_PART_LENGTH} characters`);
    }
    return t || undefined;
}
// ---------------------------------------------------------------------------
// vCard (RFC 6350) pieces, shared by the API's and the web app's emitters
// ---------------------------------------------------------------------------
/** Escape a vCard text value (backslash, newline, comma, semicolon). */
export function vcardEscape(value) {
    return value
        .replace(/\\/g, '\\\\')
        .replace(/\r?\n/g, '\\n')
        .replace(/,/g, '\\,')
        .replace(/;/g, '\\;');
}
/**
 * The ORG line: organisation name, then the department as its second
 * component (`ORG:Acme Ltd;Sales`). Null when there's neither.
 */
export function vcardOrg(company, department) {
    if (!company && !department)
        return null;
    const parts = [vcardEscape(company ?? '')];
    if (department)
        parts.push(vcardEscape(department));
    return `ORG:${parts.join(';')}`;
}
/**
 * The ADR line: PO box and extended address empty, then street, locality,
 * region, postal code and country name. Null when there's no address.
 */
export function vcardAdr(a) {
    if (!hasAddress(a))
        return null;
    const country = a.country ? countryName(a.country, 'en') : '';
    const parts = ['', '', a.street ?? '', a.city ?? '', a.region ?? '', a.postalCode ?? '', country];
    return `ADR:${parts.map(vcardEscape).join(';')}`;
}
/** A maps search link for the address (opens the maps app on phones). */
export function mapLink(a) {
    if (!hasAddress(a))
        return null;
    const q = addressLines(a, 'en').join(', ');
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
}
//# sourceMappingURL=address.js.map